import React, { useState } from 'react';
import { CreditCard } from 'lucide-react';
import { CurrentSubscription, SubscriptionPlan, trialDaysLeft } from '../../../types/subscription.types';

interface BillingCycleCardProps {
  subscription: CurrentSubscription;
  currentPlan: SubscriptionPlan | undefined;
  onManageBilling: () => void;
  onReactivate: () => Promise<void>;
}

const formatDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '—';

const BillingCycleCard: React.FC<BillingCycleCardProps> = ({
  subscription,
  currentPlan,
  onManageBilling,
  onReactivate,
}) => {
  const [reactivating, setReactivating] = useState(false);

  const isCanceling = subscription.cancel_at_period_end;
  const daysLeft = trialDaysLeft(subscription.current_period_end);
  const periodStartLabel = formatDate(subscription.current_period_start);
  const periodEndLabel = formatDate(subscription.current_period_end);

  const handleReactivate = async () => {
    setReactivating(true);
    try {
      await onReactivate();
    } finally {
      setReactivating(false);
    }
  };

  const timelineItems = [
    { label: 'Billing period started', date: periodStartLabel, done: true },
    {
      label: daysLeft === 0 ? 'Today' : `Today · ${daysLeft ?? 0} ${daysLeft === 1 ? 'day' : 'days'} left`,
      date: 'Current period',
      done: false,
      current: true,
    },
    {
      label: isCanceling ? 'Plan ends · moves to Discover' : `Renews · ${currentPlan?.price_display ?? ''}/mo`,
      date: periodEndLabel,
      done: false,
      last: true,
    },
  ];

  return (
    <div className="mb-6 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm sm:mb-8">
      <div className="border-b border-gray-100 px-5 py-5 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-extrabold text-secondary">{subscription.plan_display_name}</h2>
            {isCanceling && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-[#E7C9CA] bg-primary/10 px-2.5 py-1 text-xs font-bold text-primary">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                Cancellation scheduled
              </span>
            )}
          </div>
          {currentPlan && (
            <div className="text-sm text-gray-500">
              {currentPlan.price_display}
              <span className="text-xs">/mo</span>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2.5 px-5 py-4 text-sm sm:px-6">
        {isCanceling ? (
          <p className="text-secondary">
            Your plan is canceled and won&apos;t renew. You&apos;ll keep full access until{' '}
            <b className="font-bold">{periodEndLabel}</b>, then move to the Discover plan.
          </p>
        ) : (
          <p className="text-secondary">
            Your {subscription.plan_display_name} plan renews <b className="font-bold">{periodEndLabel}</b> at{' '}
            <b className="font-bold">{currentPlan?.price_display ?? ''}/mo</b>.
          </p>
        )}
      </div>

      <div className="px-5 pb-5 sm:px-6">
        <div className="rounded-xl border border-gray-100 bg-gray-50 px-4 pb-1 pt-4">
          <div className="mb-4 text-[11px] font-bold uppercase tracking-wide text-gray-400">Billing cycle</div>
          {timelineItems.map((item) => (
            <div key={item.label} className="relative flex gap-3 pb-4">
              <div className="flex flex-none flex-col items-center">
                <span
                  className={`h-3.5 w-3.5 rounded-full border-[2.5px] ${
                    item.done
                      ? 'border-map-green bg-map-green'
                      : item.current
                      ? 'border-primary bg-white'
                      : 'border-gray-300 bg-white'
                  }`}
                />
                {!item.last && <span className={`mt-0.5 w-0.5 flex-1 ${item.done ? 'bg-map-green/40' : 'bg-gray-200'}`} />}
              </div>
              <div className="-mt-0.5 pb-0.5">
                <div className={`text-[13.5px] ${item.current ? 'font-bold text-primary' : 'font-semibold text-secondary'}`}>
                  {item.label}
                </div>
                <div className="mt-0.5 text-xs text-gray-400">{item.date}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {isCanceling && (
        <div className="flex flex-wrap gap-3 px-5 pb-5 sm:px-6">
          <button
            onClick={handleReactivate}
            disabled={reactivating}
            className="min-w-[140px] flex-1 rounded-xl bg-primary py-3 text-sm font-bold text-white hover:bg-primary-hover disabled:opacity-60"
          >
            {reactivating ? 'Reactivating…' : 'Reactivate subscription'}
          </button>
        </div>
      )}

      <div className="border-t border-gray-100 px-5 py-4 sm:px-6">
        <button onClick={onManageBilling} className="flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-primary">
          <CreditCard className="h-4 w-4" />
          Manage billing
        </button>
      </div>
    </div>
  );
};

export default BillingCycleCard;
